import { useEffect, useMemo, useRef, useState } from "react";

import amitAsset from "@/assets/doctors/dr-amit-kumar.png.asset.json";
import pulkitAsset from "@/assets/doctors/dr-pulkit-gupta.jpeg.asset.json";
import kashishAsset from "@/assets/doctors/dr-kashish-sachdeva.webp.asset.json";
import narendraAsset from "@/assets/doctors/dr-narendra-kishor-mandal.jpeg.asset.json";
import pankajAsset from "@/assets/dr-pankaj-sharma.jpg.asset.json";
import pilesAsset from "@/assets/home/piles.jpeg.asset.json";
import { SiteFooter } from "@/components/SiteFooter";
import { SiteHeader } from "@/components/SiteHeader";
import { separateLandingPageDisclaimer } from "@/lib/landing-page-disclaimer";
import { LP_CHROME_CSS } from "@/lib/lp-chrome-css";
import { notifyForm } from "@/lib/notifyForm";
import { PILES_TREATMENT_CSS, PILES_TREATMENT_HTML } from "@/lib/piles-treatment-lp";

export type PilesTreatmentCity = "Agra" | "Gurgaon" | "Haridwar" | "Ghaziabad" | "Muzaffarnagar";

type CityConfig = {
  slug: string;
  doctor: string;
  doctorImage: string;
  doctorRole: string;
  doctorShortQuals: string;
  doctorEducation: string;
  doctorFellowships: string;
  address: string;
  addressShort: string;
  mapUrl: string;
};

export const PILES_TREATMENT_CITIES: Record<PilesTreatmentCity, CityConfig> = {
  Agra: {
    slug: "agra",
    doctor: "Dr. Amit Kumar",
    doctorImage: amitAsset.url,
    doctorRole: "Consultant Laparoscopic & Colorectal Surgeon",
    doctorShortQuals: "MBBS · MS (Surgery) · FMAS · FIAGES · FISCP",
    doctorEducation: "MBBS, MS (General Surgery) — Gold Medalist, Sarojini Naidu Medical College",
    doctorFellowships: "FMAS, FIAGES and FISCP — minimal-access and colorectal surgery",
    address: "Plot No-1 A, Mauja Bhogipura, Bodla Road, Maruti Estate, Shahganj, Agra, UP 282012",
    addressShort: "Bodla Road, Maruti Estate, Shahganj",
    mapUrl: "https://maps.google.com/maps?q=Healic+Multispeciality+Hospital+Bodla+Road+Shahganj+Agra&z=15&output=embed",
  },
  Gurgaon: {
    slug: "gurgaon",
    doctor: "Dr. Narendra Kishor Mandal",
    doctorImage: narendraAsset.url,
    doctorRole: "Senior General & Laparoscopic Surgeon",
    doctorShortQuals: "MBBS · MS (General Surgery) · DNB (Urology)",
    doctorEducation: "MBBS, MS (General Surgery) and DNB (Urology)",
    doctorFellowships: "30+ years of surgical experience",
    address: "68 Avenue, Sector 68, Badshahpur, Sohna Road, Gurugram, Haryana 122101",
    addressShort: "68 Avenue, Sector 68, Sohna Road",
    mapUrl: "https://maps.google.com/maps?q=Healic+Multispeciality+Hospital+68+Avenue+Sector+68+Gurugram&z=15&output=embed",
  },
  Haridwar: {
    slug: "haridwar",
    doctor: "Dr. Kashish Sachdeva",
    doctorImage: kashishAsset.url,
    doctorRole: "Consultant General, Laparoscopic & Laser Surgeon",
    doctorShortQuals: "MBBS · DNB (General Surgery) · FMAS · EFIAGES · FIEU",
    doctorEducation: "MBBS, DNB (General Surgery) and FMAS",
    doctorFellowships: "EFIAGES and FIEU — gastro-endoscopy and urology",
    address: "Ranipur More, 4 Old Haridwar Main Road, opposite Pizza Hut, Haridwar, Uttarakhand 249401",
    addressShort: "Ranipur More, Haridwar Main Road",
    mapUrl: "https://maps.google.com/maps?q=Healic+Multispeciality+Hospital+Ranipur+More+Haridwar&z=15&output=embed",
  },
  Ghaziabad: {
    slug: "ghaziabad",
    doctor: "Dr. Pulkit Gupta",
    doctorImage: pulkitAsset.url,
    doctorRole: "General & Laparoscopic Surgeon",
    doctorShortQuals: "MBBS · MS (General Surgery) · FMAS · FISCP",
    doctorEducation: "MBBS — Dr. D.Y. Patil Medical College, Kolhapur · MS (General Surgery) — KLE University, Belgaum",
    doctorFellowships: "FMAS and FISCP — minimal-access and colorectal surgery · 6+ years of experience",
    address: "C-3, Saya Zenith, Plot No. GH-11, near CISF Camp, Ahinsa Khand 2, Indirapuram, Ghaziabad, UP 201014",
    addressShort: "Saya Zenith, Ahinsa Khand 2, Indirapuram",
    mapUrl: "https://maps.google.com/maps?q=Healic+Saya+Zenith+Indirapuram+Ghaziabad&z=15&output=embed",
  },
  Muzaffarnagar: {
    slug: "muzaffarnagar",
    doctor: "Dr. Pankaj Sharma",
    doctorImage: pankajAsset.url,
    doctorRole: "Senior Laparoscopic, Thoracic & General Surgeon",
    doctorShortQuals: "MBBS · MS (General Surgery) · FICS · FMAS · FIAGES",
    doctorEducation: "MBBS and MS (General Surgery) — LLRM Medical College, Meerut · Thoracic surgery training — Safdarjung Hospital, New Delhi",
    doctorFellowships: "FICS, FMAS and FIAGES — laparoscopic and gastrointestinal endo-surgery · 30+ years of experience",
    address: "37/10 Circular Road, Santosh Vihar, Civil Lines South, Muzaffarnagar, UP 251001",
    addressShort: "Circular Road, Civil Lines South",
    mapUrl: "https://maps.google.com/maps?q=Healic+Hospital+37%2F10+Circular+Road+Civil+Lines+Muzaffarnagar&z=15&output=embed",
  },
};

function istHour() {
  try {
    return Number(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata", hour: "2-digit", hour12: false }));
  } catch {
    return 12;
  }
}

function renderCityHtml(city: PilesTreatmentCity) {
  const config = PILES_TREATMENT_CITIES[city];
  const values: Record<string, string> = {
    CITY: city,
    SLUG: config.slug,
    DOCTOR_NAME: config.doctor,
    DOCTOR_IMAGE: config.doctorImage,
    DOCTOR_ROLE: config.doctorRole,
    DOCTOR_SHORT_QUALS: config.doctorShortQuals,
    DOCTOR_EDUCATION: config.doctorEducation,
    DOCTOR_FELLOWSHIPS: config.doctorFellowships,
    TREATMENT_IMAGE: pilesAsset.url,
    ADDRESS: config.address,
    ADDRESS_SHORT: config.addressShort,
    MAP_URL: config.mapUrl.replaceAll("&", "&amp;"),
  };
  const html = Object.entries(values).reduce(
    (html, [key, value]) => html.replaceAll(`{{${key}}}`, value),
    PILES_TREATMENT_HTML,
  );
  if (city === "Ghaziabad") return html;
  return html.replace(
    /<!-- ============ LEAD IMAGE ============ -->[\s\S]*?(?=<!-- ============ WHAT ARE PILES \/ LASER ============ -->)/,
    "",
  );
}

const COMPACT_TREATMENT_CSS = `
@scope (.lp-piles-treatment.compact-treatment) {
  .hero { padding: 26px 0 30px; }
  .hero-grid { grid-template-columns: 1.18fr .82fr; gap: 28px; align-items: center; }
  .eyebrow { margin-bottom: 8px; }
  .hero h1 { font-size: clamp(28px, 4vw, 40px); margin-bottom: 8px; }
  .hero-sub { max-width: 580px; margin-bottom: 14px; }
  .hero-intro, .symptom-check, .info-box { display: none; }
  .price-block { margin-bottom: 14px; padding: 11px 14px; }
  .hero-panel { max-width: 100%; padding: 20px; }
  .hero-panel ul { gap: 9px; margin-bottom: 14px; }
  .hero-panel li:nth-child(n+4) { display: none; }
  .hero-doc { padding-top: 13px; }
  .doctor-photo-image { width: 72px; height: 72px; object-fit: cover; }
  .trust-strip { padding: 16px 0; }
  .inline-form-section { padding: 26px 0; }
  @media (max-width: 900px) {
    .hero-grid { grid-template-columns: 1fr; gap: 20px; }
    .hero-panel { max-width: 100%; }
  }
  @media (max-width: 600px) {
    .hero { padding: 20px 0 24px; }
    .hero h1 { font-size: 30px; }
    .hero-sub { font-size: 15px; line-height: 1.45; }
    .price-block { margin-bottom: 12px; }
    .hero-ctas .btn { flex: 1; padding: 11px 12px; }
    .hero-panel { padding: 17px 16px; }
  }
}
`;

export function PilesTreatmentCityPage({ city }: { city: PilesTreatmentCity }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [ctaMode, setCtaMode] = useState<"active" | "off">("active");
  const config = PILES_TREATMENT_CITIES[city];
  const compact = city !== "Ghaziabad";
  const html = useMemo(() => separateLandingPageDisclaimer(renderCityHtml(city)), [city]);

  useEffect(() => setCtaMode(istHour() >= 9 && istHour() < 20 ? "active" : "off"), []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const cleanups: Array<() => void> = [];
    const leadModal = root.querySelector<HTMLElement>("#lead-modal");
    const callModal = root.querySelector<HTMLElement>("#call-modal");
    const formContainer = root.querySelector<HTMLElement>("#modal-form-container");
    const thankYou = root.querySelector<HTMLElement>("#thank-you");

    const openModal = (modal: HTMLElement | null) => {
      if (!modal) return;
      modal.classList.add("open");
      modal.setAttribute("aria-hidden", "false");
      document.documentElement.classList.add("modal-open");
    };
    const closeModal = (modal: HTMLElement | null) => {
      if (!modal) return;
      modal.classList.remove("open");
      modal.setAttribute("aria-hidden", "true");
      if (!root.querySelector(".modal-backdrop.open")) document.documentElement.classList.remove("modal-open");
    };
    const openLeadForm = (doctor = "") => {
      const doctorField = root.querySelector<HTMLInputElement>("#m-doctor");
      const pageField = root.querySelector<HTMLInputElement>("#m-pageurl");
      if (doctorField) doctorField.value = doctor;
      if (pageField) pageField.value = window.location.href;
      if (formContainer) formContainer.style.display = "";
      thankYou?.classList.remove("active");
      openModal(leadModal);
      window.setTimeout(() => root.querySelector<HTMLInputElement>("#m-name")?.focus({ preventScroll: true }), 300);
    };

    const clickHandler = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const closeButton = target?.closest("[data-modal-close]");
      if (closeButton) {
        event.preventDefault();
        closeModal(closeButton.closest(".modal-backdrop") as HTMLElement | null);
        return;
      }
      const opener = target?.closest("[data-open-form]");
      if (opener) {
        event.preventDefault();
        openLeadForm(opener.getAttribute("data-doctor") || "");
      }
    };
    root.addEventListener("click", clickHandler);
    cleanups.push(() => root.removeEventListener("click", clickHandler));

    root.querySelectorAll<HTMLAnchorElement>('a[href^="tel:"]').forEach((link) => {
      const handler = (event: Event) => {
        if (root.dataset.ctaMode === "off") {
          event.preventDefault();
          openLeadForm();
        } else if (window.matchMedia("(min-width: 900px) and (pointer: fine)").matches) {
          event.preventDefault();
          openModal(callModal);
        }
      };
      link.addEventListener("click", handler);
      cleanups.push(() => link.removeEventListener("click", handler));
    });

    root.querySelectorAll<HTMLElement>(".modal-backdrop").forEach((backdrop) => {
      const handler = (event: Event) => event.target === backdrop && closeModal(backdrop);
      backdrop.addEventListener("click", handler);
      cleanups.push(() => backdrop.removeEventListener("click", handler));
    });

    const keyHandler = (event: KeyboardEvent) => {
      if (event.key === "Escape") root.querySelectorAll<HTMLElement>(".modal-backdrop.open").forEach(closeModal);
    };
    document.addEventListener("keydown", keyHandler);
    cleanups.push(() => document.removeEventListener("keydown", keyHandler));

    root.querySelectorAll<HTMLFormElement>("form.inline-form-right, #modal-form").forEach((form) => {
      const handler = (event: Event) => {
        event.preventDefault();
        const data = new FormData(form);
        const name = String(data.get("name") ?? "").trim();
        void notifyForm({
          formType: "Piles Treatment Consultation",
          source: `${city} Piles Treatment Page`,
          fields: [
            { label: "Name", value: name },
            { label: "Phone", value: String(data.get("phone") ?? "").trim() },
            { label: "Treatment", value: "Piles Treatment" },
            { label: "City", value: city },
            { label: "Doctor", value: String(data.get("doctor") ?? config.doctor) },
            { label: "Page", value: window.location.href },
          ],
        });
        const thankYouName = root.querySelector("#ty-name");
        if (thankYouName) thankYouName.textContent = (name || "friend").split(" ")[0];
        form.reset();
        if (formContainer) formContainer.style.display = "none";
        thankYou?.classList.add("active");
        openModal(leadModal);
      };
      form.addEventListener("submit", handler);
      cleanups.push(() => form.removeEventListener("submit", handler));
    });

    const mapFrame = root.querySelector<HTMLElement>("#map-frame");
    if (mapFrame && "IntersectionObserver" in window) {
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting) || mapFrame.querySelector("iframe")) return;
        const src = mapFrame.dataset.mapSrc;
        if (!src) return;
        const iframe = document.createElement("iframe");
        iframe.src = src;
        iframe.loading = "lazy";
        iframe.title = `Healic Multispeciality Hospital ${city} map`;
        iframe.referrerPolicy = "no-referrer-when-downgrade";
        mapFrame.appendChild(iframe);
        observer.disconnect();
      }, { rootMargin: "300px" });
      observer.observe(mapFrame);
      cleanups.push(() => observer.disconnect());
    }

    return () => {
      cleanups.forEach((cleanup) => cleanup());
      document.documentElement.classList.remove("modal-open");
    };
  }, [city, config.doctor]);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: PILES_TREATMENT_CSS + LP_CHROME_CSS + COMPACT_TREATMENT_CSS }} />
      <div className="lp-chrome"><SiteHeader /></div>
      <div ref={rootRef} className={`lp-piles-treatment${compact ? " compact-treatment" : ""}`} data-cta-mode={ctaMode} dangerouslySetInnerHTML={{ __html: html.contentHtml }} />
      <div className="lp-chrome"><SiteFooter /></div>
      {html.disclaimerHtml ? <div className="lp-piles-treatment" dangerouslySetInnerHTML={{ __html: html.disclaimerHtml }} /> : null}
    </>
  );
}
